import React from 'react';
import { Scissors } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLang } from '@/hooks/useLang';
import { buildLangPath, stripLangPrefix } from '@/utils/routeBuilders';

const HeroSection = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('oral-surgery');
  const { lang } = useLang();

  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault();

    const scrollToContact = () => {
      const contactForm = document.getElementById('contact-form');
      if (contactForm) {
        const yOffset = -100; // Offset to account for fixed header
        const y =
          contactForm.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    };

    // Navigate to language-prefixed home and scroll to contact form.
    if (stripLangPrefix(window.location.pathname) !== '/') {
      navigate(buildLangPath(lang, '/'));
      setTimeout(scrollToContact, 100);
    } else {
      scrollToContact();
    }
  };

  return (
    <section className="relative pb-20 pt-32 md:pt-40 lg:pt-48 overflow-hidden">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-white to-dental-light/20 pointer-events-none"></div>
      <div className="absolute top-0 right-0 w-full h-full bg-[radial-gradient(circle_at_top_right,rgba(101,163,182,0.1),transparent_70%)]"></div>
      
      {/* Decorative Elements */}
      <div className="absolute top-40 -left-20 w-64 h-64 bg-dental-mint/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-72 h-72 bg-dental-coral/10 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col md:flex-row items-center gap-12">
          {/* Hero Content */}
          <div className="md:w-1/2 reveal" data-direction="left">
            <div className="inline-flex items-center bg-dental-light/30 px-4 py-2 rounded-full mb-6">
              <img 
                src="/images/Oral Surgery.png"
                  alt={t('hero.badgeIconAlt')}
                className="w-8 h-8 mr-2"
              />
                <span className="text-dental-dark/80 text-sm font-medium">{t('hero.badgeText')}</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-6 text-gradient-primary">
                {t('hero.titleLine1')} <br />{t('hero.titleLine2')}
            </h1>
            
            <p className="text-lg text-dental-dark/80 mb-8 max-w-xl">
                {t('hero.description')}
            </p>
            
            <div className="flex flex-wrap gap-4">
              <a 
                  href={buildLangPath(lang, '/')}
                className="btn-primary"
                onClick={handleContactClick}
              >
                {t('hero.ctas.primary')}
              </a>
              <a href="#process" className="btn-outline">
                {t('hero.ctas.secondary')}
              </a>
            </div>
          </div>
          
          {/* Hero Image */}
          <div className="md:w-1/2 reveal" data-direction="right">
            <div className="relative">
              <div className="bg-white p-2 rounded-2xl shadow-blue-glow overflow-hidden">
                <img 
                  src="/images/Surgery.jpg" 
                  alt={t('hero.heroImageAlt')} 
                  className="w-full h-[500px] object-cover rounded-xl"
                />
              </div>
              
              {/* Stats Card */}
              <div className="absolute -bottom-10 -left-10 bg-white rounded-xl shadow-lg p-4 md:p-6 reveal" data-direction="up" data-delay="300">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-dental-dark/60 text-sm">{t('hero.stats.successRateLabel')}</p>
                    <p className="text-2xl font-bold text-dental-purple">{t('hero.stats.successRateValue')}</p>
                  </div>
                  <div>
                    <p className="text-dental-dark/60 text-sm">{t('hero.stats.recoveryLabel')}</p>
                    <p className="text-2xl font-bold text-dental-coral">{t('hero.stats.recoveryValue')}</p>
                  </div>
                </div>
              </div>
              
              {/* Experience Card */}
              <div className="absolute -top-6 -right-6 bg-dental gradient-card rounded-xl shadow-lg p-4 text-white reveal" data-direction="down" data-delay="300">
                <p className="text-sm font-medium">{t('hero.experience.label')}</p>
                <p className="text-xl font-semibold">{t('hero.experience.value')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
